import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import smileRingtoneUri from '../../assets/audio/smile-ringtone.mp3?url';
import { gracefullyPlayAudio } from '../../audio';
import { useActiveBrand } from '../../components/branding/useActiveBrand';
import { NOTIFICATION_VOLUME } from '../../config';
import { logger } from '../../datadog/logger';
import { useGetTelehealthSessionInfoCallback } from '../../hooks';
import { useAdmissionFlowCallback } from '../../hooks/useAdmissionFlow';
import { setAlertTitle } from '../../metadata';
import { twilioSyncClientAtom } from '../../session';
import { debounce, withRetry } from '../../utils';
import { roomTokenAtom } from '../../video-sdk';
import { DebugMutex } from '../../video-sdk/core/mutex';
import { zoomTokenAtom } from '../../video-sdk/zoom';
import { Modal, useOpenVisitorModalCallback } from '../modals/useOpenVisitorModalCallback';
import { hideSystemNotificationsPermissionUserGesturePromptToast } from '../notifications';
import {
  VisitorState,
  apiTokenAtom,
  preSessionVibesOpenAtom,
  sessionAppointmentShortIdAtom,
  sessionConversationSidAtom,
  visitorAccessDocSidAtom,
  visitorStateAtom,
} from '../state';
import { useVisitorId } from '../useVisitorId';
import { admissionTrackingHistory } from './admission/analytics/AdmissionTrackingHistory';
import { useShowAdmissionSystemNotificationCallback } from './admission/notifications/useShowAdmissionSystemNotificationCallback';
import { admissionAttemptsAtom, admissionTimeAtom, isAdmissionModalOpenAtom } from './admission/state';
import { useVisitorConnectRoomCallback } from './admission/useVisitorConnectRoomCallback';
import { useUpdateVisitorPresenceCallback } from './useUpdateVisitorPresenceCallback';

const LOAD_TIME_MS = 1000;
const DEBOUNCE_TIME_MS = 1000;

enum VisitorAccessDocEventType {
  GRANTED = 'access-granted',
  DENIED = 'access-denied',
}

type VisitorAccessDocData = {
  eventType: VisitorAccessDocEventType;
  token: string;
  /**
   * A token that can be used to communicate with the Telehealth API as a client
   * user
   */
  apiToken?: string;
  conversationSid?: string;
  zoomJwt: string;
  admissionTime?: string;

  /**
   * A unique ID generated on the provider side to represent the admission
   * transaction end-to-end. Used in user tracking.
   */
  admissionTransactionId?: string;
};

const playJoiningRoomSound = debounce(() => {
  const ringtone = new Audio(smileRingtoneUri);
  ringtone.volume = NOTIFICATION_VOLUME;
  gracefullyPlayAudio(ringtone);
}, DEBOUNCE_TIME_MS);

type HookOptions = {
  onDone?: () => void;
};

/**
 * Gracefully fetches the session appointment short ID and sets it in Jotai
 */
function useSetSessionAppointmentShortIdCallback() {
  const { providerShortId } = useParams<{ providerShortId: string }>();
  const { patientShortId } = useParams<{ patientShortId: string }>();
  const setSessionAppointmentShortId = useSetAtom(sessionAppointmentShortIdAtom);
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();

  return useCallback(async () => {
    try {
      const { data } = await getTelehealthSessionInfo({
        variables: {
          patientShortId,
          providerShortId,
        },
      });
      setSessionAppointmentShortId(data?.telehealthSessionInfo?.appointment?.shortId);
    } catch (error) {
      logger.error(
        'Failed to fetch/set appointment short ID for session',
        {
          patientShortId,
          providerShortId,
        },
        error as Error,
      );
    }
  }, [getTelehealthSessionInfo, patientShortId, providerShortId, setSessionAppointmentShortId]);
}

const roomMutex = new DebugMutex('roomMutex');
export function useTransitionToMeetingRoom() {
  const setVisitorStateAtom = useSetAtom(visitorStateAtom);
  const connectRoom = useVisitorConnectRoomCallback();

  return useCallback(
    ({ onDone }: HookOptions = {}) => {
      // if multiple calls to transition to the meeting room happen simulatenously
      // the first one "wins" and the rest can just no-op.
      if (roomMutex.isLocked()) return;

      roomMutex.runExclusive(async () => {
        playJoiningRoomSound();
        setVisitorStateAtom(VisitorState.LOADING);

        // effectively sleep, need to await this way so that setTimeout runs async
        // otherwise, fire-and-forget will release the mutex before this timeout has elapsed!
        await new Promise((resolve) => setTimeout(resolve, LOAD_TIME_MS));

        const room = await connectRoom();

        if (!room) {
          setVisitorStateAtom(VisitorState.WAITING);
          return;
        }

        onDone?.();
      }, 'useTransitionToMeetingRoom');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectRoom],
  );
}

export function useWatchForRoomToken() {
  const activeBrand = useActiveBrand();
  const t = useTranslate();
  const visitorUuid = useVisitorId();
  const syncClient = useAtomValue(twilioSyncClientAtom);
  const visitorAccessDocSid = useAtomValue(visitorAccessDocSidAtom);
  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const setRoomToken = useSetAtom(roomTokenAtom);
  const setZoomToken = useSetAtom(zoomTokenAtom);
  const setApiToken = useSetAtom(apiTokenAtom);
  const setSessionConversationSid = useSetAtom(sessionConversationSidAtom);
  const setSessionAppointmentShortId = useSetSessionAppointmentShortIdCallback();
  const setIsAdmissionModalOpen = useSetAtom(isAdmissionModalOpenAtom);
  const getIsAdmissionModalOpen = useAtomCallback(useCallback((get) => get(isAdmissionModalOpenAtom), []));
  const setAdmissionAttempts = useSetAtom(admissionAttemptsAtom);
  const openVisitorModal = useOpenVisitorModalCallback();
  const setAdmissionTime = useSetAtom(admissionTimeAtom);
  const updateVisitorPresence = useUpdateVisitorPresenceCallback();
  const setPreSessionVibesOpen = useSetAtom(preSessionVibesOpenAtom);
  const getAdmissionFlow = useAdmissionFlowCallback();
  const getIsJoiningRoom = useAtomCallback(useCallback((get) => get(visitorStateAtom) === VisitorState.JOINING, []));
  const showAdmissionSystemNotification = useShowAdmissionSystemNotificationCallback();

  useEffect(
    function watchForRoomToken() {
      (async () => {
        if (!syncClient || !visitorAccessDocSid) return;
        logger.info('Initializing visitor-access document', {
          docSid: visitorAccessDocSid,
        });
        const visitorAccessDoc = await syncClient.document(visitorAccessDocSid);
        logger.info('Initialized visitor-access document', {
          docSid: visitorAccessDocSid,
        });
        const handleVisitorAccessDocUpdate = ({
          eventType,
          token,
          apiToken,
          conversationSid,
          zoomJwt,
          admissionTime,
          admissionTransactionId,
        }: VisitorAccessDocData) => {
          logger.info('Received visitor-access document update', {
            hasToken: !!token,
            eventType,
            conversationSid,
            admissionTime,
            admissionTransactionId,
          });
          if (admissionTransactionId) {
            admissionTrackingHistory.setTransactionId(admissionTransactionId);
          }
          setRoomToken(token);
          setZoomToken(zoomJwt);
          setApiToken(apiToken);
          setSessionConversationSid(conversationSid);

          switch (eventType) {
            case VisitorAccessDocEventType.GRANTED: {
              // This is not session blocking so fire and forget
              setSessionAppointmentShortId();
              setAdmissionAttempts((attempts) => attempts + 1);

              hideSystemNotificationsPermissionUserGesturePromptToast();

              switch (getAdmissionFlow()) {
                // TODO(telehealth_automatic_admission_xp): remove if the Test group ships
                case 'manual':
                  if (admissionTime) {
                    // Important to relay the admission time back for the
                    // provider side to properly wait for the client to join
                    setAdmissionTime(admissionTime);
                    withRetry(() => updateVisitorPresence(), {
                      retries: 2,
                      initialDelayMs: 500,
                    });
                  }

                  setPreSessionVibesOpen(false);
                  if (!getIsAdmissionModalOpen() && !getIsJoiningRoom()) {
                    showAdmissionSystemNotification();
                    playJoiningRoomSound();
                    setAlertTitle(t('telehealth-core:meta.joinYourSession'), activeBrand);
                    openVisitorModal(Modal.ADMISSION);
                  }
                  break;
                case 'auto':
                default:
                  showAdmissionSystemNotification();
                  transitionToMeetingRoom();
              }
              return;
            }
            case VisitorAccessDocEventType.DENIED:
              // TODO: Handle denied event type
              return;
            default:
              logger.error('Received an unknown visitor-access event type', {
                eventType,
                visitorAccessDocSid,
              });
              return;
          }
        };

        // Consuming initial data can lead to unexpected behavior - see TREE-844
        // const initialData = visitorAccessDoc.data as VisitorAccessDocData | undefined;
        // if (initialData?.eventType) {
        //   handleVisitorAccessDocUpdate(initialData);
        // }

        visitorAccessDoc.on('updated', ({ data }: { data: VisitorAccessDocData }) => {
          handleVisitorAccessDocUpdate(data);
        });
      })();
    },
    [
      t,
      visitorUuid,
      syncClient,
      visitorAccessDocSid,
      transitionToMeetingRoom,
      setSessionConversationSid,
      setRoomToken,
      setApiToken,
      setSessionAppointmentShortId,
      setZoomToken,
      setIsAdmissionModalOpen,
      getIsAdmissionModalOpen,
      setAdmissionAttempts,
      openVisitorModal,
      setAdmissionTime,
      updateVisitorPresence,
      setPreSessionVibesOpen,
      getAdmissionFlow,
      getIsJoiningRoom,
      activeBrand,
      showAdmissionSystemNotification,
    ],
  );
}
